import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

const ConfirmContext = createContext(null);

/**
 * Hosts the app's confirmation dialog. Wrap the app once; components then call
 * `const confirm = useConfirm()` and `if (!(await confirm({ ... }))) return;`.
 */
export function ConfirmProvider({ children }) {
    const [options, setOptions] = useState(null);
    const resolver = useRef(null);

    const confirm = useCallback((next) => new Promise((resolve) => {
        // A second request replaces the first, which counts as cancelled.
        if (resolver.current) resolver.current(false);
        resolver.current = resolve;
        setOptions(next);
    }), []);

    const close = (result) => {
        if (resolver.current) resolver.current(result);
        resolver.current = null;
        setOptions(null);
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <Modal show={Boolean(options)} onHide={() => close(false)} centered>
                {options?.title && (
                    <Modal.Header closeButton>
                        <Modal.Title>{options.title}</Modal.Title>
                    </Modal.Header>
                )}
                <Modal.Body style={{ whiteSpace: 'pre-line' }}>{options?.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => close(false)}>
                        {options?.cancelLabel || 'Cancel'}
                    </Button>
                    <Button variant={options?.danger ? 'danger' : 'primary'} onClick={() => close(true)} autoFocus>
                        {options?.confirmLabel || 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </ConfirmContext.Provider>
    );
}

/**
 * Returns confirm({ title?, message, confirmLabel?, cancelLabel?, danger? }), which resolves
 * true when the user confirms and false when they cancel or close the dialog.
 */
export function useConfirm() {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>');
    return confirm;
}
